<script setup lang="ts">
import { computed, onMounted, watch } from 'vue';
import { RouterView, useRoute } from 'vue-router';
import { useToast } from './composables/useToast';
import { useClientAuthStore } from './stores/auth';
import { useAppMetaStore } from './stores/appMeta';
import { initClientPushNotifications } from './composables/usePushNotifications';
import ClientBottomNav from './components/layout/ClientBottomNav.vue';
import ChatRealtimeBridge from './components/chat/ChatRealtimeBridge.vue';
import MobileGate from '../shared/components/MobileGate.vue';
import EnvRibbon from '../../shared/EnvRibbon.vue';
import VersionBadge from '../../shared/VersionBadge.vue';

const { toasts, removeToast } = useToast();
const auth = useClientAuthStore();
const meta = useAppMetaStore();
const route = useRoute();

watch(() => auth.isAuthenticated, (authenticated) => {
    if (authenticated) initClientPushNotifications();
}, { immediate: true });

onMounted(() => { void meta.fetchMeta(); });

// Realtime do chat vive enquanto logado + canal Web habilitado (D-CHAT-11/12).
const chatRealtimeOn = computed(() => auth.isAuthenticated && meta.webChatEnabled);

// Fluxos direcionados (auth, checkout, confirmação) escondem o menu fixo —
// não faz sentido deixar o usuário pular de aba no meio do processo.
const HIDDEN_NAV_PATHS = [
    '/login',
    '/register',
    '/completar-cadastro',
    '/esqueci-senha',
    '/reset-password',
    '/auth/google/complete',
    '/checkout/identify',
    '/checkout/address',
    '/checkout/payment',
];

const showNav = computed(() => {
    if (route.path.startsWith('/order/')) return false;
    // Thread da conversa: composer fixo no rodapé, sem menu (o inbox /mensagens
    // mantém o menu). Só a tela da conversa esconde.
    if (route.path.startsWith('/mensagens/conversa')) return false;
    return !HIDDEN_NAV_PATHS.includes(route.path);
});
</script>

<template>
    <EnvRibbon />
    <MobileGate>
        <div class="flex flex-col min-h-screen">
            <main class="flex-1" :class="showNav ? 'pb-[calc(4.5rem+env(safe-area-inset-bottom,0px))]' : ''">
                <RouterView />
            </main>
            <ClientBottomNav v-if="showNav" />
        </div>
        <ChatRealtimeBridge v-if="chatRealtimeOn" />
        <div
            class="fixed left-4 right-4 z-50 flex flex-col gap-2 pointer-events-none max-w-[420px] mx-auto"
            :class="showNav ? 'bottom-[calc(5.5rem+env(safe-area-inset-bottom,0px))]' : 'bottom-4'"
            aria-live="polite"
        >
            <div
                v-for="t in toasts"
                :key="t.id"
                class="pointer-events-auto rounded-xl shadow-lg px-4 py-3 text-left border text-[14px]"
                :style="{
                    background: 'var(--card, #fff)',
                    borderColor: 'var(--border)',
                    color: 'var(--text)',
                }"
            >
                <div class="font-bold leading-tight">{{ t.title }}</div>
                <p v-if="t.message" class="m-0 mt-1 text-[13px]" style="color: var(--muted)">{{ t.message }}</p>
                <button
                    type="button"
                    class="mt-2 text-[12px] font-semibold border-0 bg-transparent cursor-pointer p-0"
                    style="color: var(--primary)"
                    @click="removeToast(t.id)"
                >
                    Fechar
                </button>
            </div>
        </div>
        <div class="fixed bottom-1 right-2 z-40 pointer-events-none">
            <VersionBadge />
        </div>
    </MobileGate>
</template>
